import React, { PureComponent } from "react";
import styled from 'styled-components';
import Fragment from 'react-dot-fragment';

import SearchIcon from '../../../containers/assets/icon-search.svg';
import Auth from '../../../routes/Auth';
import {
  clearAppGeneralInfoCache,
  fetchAppGeneralInfo,
  pickCourseAndGame,
} from '../../../api/AppGeneralInfoService';
import { runRosterUpload } from '../../utils/roster-upload';
import { runReassignUpload, REASSIGN_TEMPLATE_CSV } from '../../utils/reassign-upload';
import { runAssignSectionsUpload, ASSIGN_SECTIONS_TEMPLATE_CSV } from '../../utils/assign-sections-upload';
import { runChangeRolesUpload, CHANGE_ROLES_TEMPLATE_CSV } from '../../utils/change-roles-upload';

const TEMPLATE_CSV = 'First Name,Last Name,email,Team\n';

const HeaderWrapper = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 320px;
`;

const SearchInputIcon = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 18px;
  width: 12px;
  height: 12px;
  display: flex;
`;

const SearchInput = styled.input`
  padding: 9px 12px 9px 40px;
  border: 1px solid #CED1D6;
  border-radius: 6px;
  width: 100%;
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);

  &::placeholder {
    color: rgba(0, 27, 59, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #55B4BC;
  }
`;

const PrimaryButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #55B4BC;
  border: 1px solid #55B4BC;
  border-radius: 6px;
  padding: 8px 16px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #FFFFFF;
  cursor: pointer;

  &:hover {
    background: #4AA1A9;
    border-color: #4AA1A9;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: #FFFFFF;
  }
`;

const UploadArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16" />
    <path d="M12 20V8" />
    <path d="M6 14l6-6 6 6" />
  </svg>
);

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalCard = styled.div`
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  padding: 32px;
  max-width: 480px;
  width: 90%;
  font-family: 'Mukta Vaani';
`;

const ModalTitle = styled.h3`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 16px;
`;

const ModalBody = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 20px;
`;

const TemplateLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #2196F3;
  font: inherit;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1e88e5;
  }
`;

// "What do you want to do?" choice screen. Appears first when the
// instructor opens the Add Students modal. Two large cards side by
// side; clicking a card advances to that flow.
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 0 0 20px;
`;

const ChoiceCard = styled.button`
  background: #FAFBFD;
  border: 1px solid #CED1D6;
  border-radius: 10px;
  padding: 20px 20px 22px;
  font: inherit;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color .15s ease, background .15s ease;

  &:hover {
    border-color: #55B4BC;
    background: #ECF6F7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: #FAFBFD;
    border-color: #CED1D6;
  }

  &:disabled:hover {
    border-color: #CED1D6;
    background: #FAFBFD;
  }
`;

const ChoiceGridThree = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin: 0 0 20px;
`;

const ChoiceCardTitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
`;

const ChoiceCardDescription = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.7);
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: #3A507B;
  cursor: pointer;
  margin: 0 0 12px;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: rgba(0, 27, 59, 0.35);
    cursor: default;
    text-decoration: none;
  }
`;

const ReassignResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 16px;
  margin: 0 0 12px;
`;

const ReassignResultCell = styled.div`
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: rgba(0, 27, 59, 0.85);
`;

const ReassignResultLabel = styled.span`
  color: rgba(0, 27, 59, 0.55);
  margin-right: 6px;
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.isDragging ? '#55B4BC' : '#CED1D6'};
  background: ${p => p.isDragging ? '#ECF6F7' : '#FAFBFD'};
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 24px;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    border-color: #55B4BC;
    background: #ECF6F7;
  }
`;

const DropZoneIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;

  svg {
    width: 32px;
    height: 32px;
    stroke: rgba(0, 27, 59, 0.6);
  }
`;

const DropZoneText = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
`;

const DropZoneHint = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 4px;
`;

const AttachedFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  background: #FAFBFD;
`;

const FileIconWrapper = styled.div`
  display: flex;

  svg {
    width: 22px;
    height: 22px;
    stroke: #55B4BC;
  }
`;

const FileMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #ECEDEF;
    color: #EC5E59;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ProgressWrapper = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProgressHint = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.6);
`;

const StepBlock = styled.div``;

const StepHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const StepTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.dimmed ? 'rgba(0, 27, 59, 0.5)' : 'rgba(0, 27, 59, 0.94)'};
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
    stroke: #2DA548;
  }
`;

const StepStatus = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: ${p => p.done ? '#2DA548' : 'rgba(0, 27, 59, 0.6)'};
`;

const ProgressBarTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #ECEDEF;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${p => p.done ? '#2DA548' : '#55B4BC'};
  width: ${p => p.percent}%;
  transition: width 250ms ease, background 200ms ease;
`;

const shimmerBar = `
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 40%;
    background: linear-gradient(90deg, transparent, #55B4BC, transparent);
    animation: rosterShimmer 1.4s ease-in-out infinite;
  }

  @keyframes rosterShimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
`;

const IndeterminateBar = styled.div`
  ${shimmerBar}
`;

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Pull a human-readable error list off a superagent error. Handles:
//   - body.detail / body.message               (DRF single-string errors)
//   - body.row_errors: [{ field: [msg], ... }] (BE roster upload shape)
//   - body.<field>: [msg]                      (top-level field errors)
// row_errors are aggregated by (field, message) with a count, so 30
// rows failing "email: Enter a valid email address." collapse to one
// line instead of 30 identical entries.
const extractBackendErrorMessages = (err) => {
  const fallback = ['Upload failed. Please try again.'];
  const body = err && err.response && err.response.body;
  if (!body) {
    return err && err.message ? [err.message] : fallback;
  }
  if (typeof body.detail === 'string') return [body.detail];
  if (typeof body.message === 'string') return [body.message];

  const lines = [];
  if (Array.isArray(body.row_errors)) {
    const agg = {};
    body.row_errors.forEach(row => {
      if (!row || typeof row !== 'object') return;
      Object.keys(row).forEach(field => {
        const raw = row[field];
        const messages = Array.isArray(raw) ? raw : [String(raw)];
        messages.forEach(msg => {
          if (!agg[field]) agg[field] = {};
          agg[field][msg] = (agg[field][msg] || 0) + 1;
        });
      });
    });
    Object.keys(agg).forEach(field => {
      Object.keys(agg[field]).forEach(msg => {
        const count = agg[field][msg];
        lines.push(`${count} row${count === 1 ? '' : 's'} with invalid ${field}: ${msg}`);
      });
    });
  }
  if (typeof body === 'object') {
    Object.keys(body).forEach(field => {
      if (field === 'row_errors') return;
      const val = body[field];
      if (Array.isArray(val)) {
        val.forEach(msg => lines.push(`${field}: ${msg}`));
      } else if (typeof val === 'string' && val) {
        lines.push(`${field}: ${val}`);
      }
    });
  }
  if (lines.length > 0) return lines;
  return err && err.message ? [err.message] : fallback;
};

const ErrorList = styled.div`
  background: #FDECEC;
  border: 1px solid #F5B5B3;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
  max-height: 240px;
  overflow-y: auto;
`;

const ErrorHeadline = styled.div`
  font-weight: 700;
  font-size: 15px;
  line-height: 22px;
  color: #B0322D;
  margin-bottom: 8px;
`;

const ErrorItem = styled.div`
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);

  & + & {
    margin-top: 4px;
  }
`;

const SuccessBox = styled.div`
  background: #ECF7EE;
  border: 1px solid #A8D9B0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  background: #F5F6F7;
  border: 1px solid #D5D9DD;
  border-radius: 6px;
  padding: 8px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.8);
  cursor: pointer;

  &:hover {
    background: #ECEDEF;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UploadButton = styled.button`
  background: #55B4BC;
  border: 1px solid #55B4BC;
  border-radius: 6px;
  padding: 8px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 14px;
  color: #FFFFFF;
  cursor: pointer;

  &:hover {
    background: #4AA1A9;
    border-color: #4AA1A9;
  }

  &:disabled {
    background: #B7DDE0;
    border-color: #B7DDE0;
    cursor: not-allowed;
  }
`;

const UploadCloudIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const triggerTemplateDownload = () => {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'teams-roles-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const triggerReassignTemplateDownload = () => {
  const blob = new Blob([REASSIGN_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'reassign-teams-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const triggerAssignSectionsTemplateDownload = () => {
  const blob = new Blob([ASSIGN_SECTIONS_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'assign-sections-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const triggerChangeRolesTemplateDownload = () => {
  const blob = new Blob([CHANGE_ROLES_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'change-roles-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STEP_TWO_STAGES = ['resolving_roles', 'uploading_game'];

const computeStepState = (stage, pollMatched, pollTotal) => {
  const step1 = { percent: 0, status: 'Waiting to start', done: false, indeterminate: false };
  const step2 = { percent: 0, status: 'Waiting to start', done: false, indeterminate: false };

  const step1Complete = STEP_TWO_STAGES.indexOf(stage) >= 0 || stage === 'success';
  const step2Complete = stage === 'success';

  if (step1Complete) {
    step1.percent = 100;
    step1.status = 'Complete';
    step1.done = true;
  } else if (stage === 'parsing' || stage === 'validating') {
    step1.percent = 5;
    step1.status = 'Preparing your file';
    step1.indeterminate = true;
  } else if (stage === 'checking_existing') {
    step1.percent = 10;
    step1.status = 'Checking for existing course members';
    step1.indeterminate = true;
  } else if (stage === 'uploading_students') {
    step1.percent = 15;
    step1.status = 'Uploading roster to your course';
    step1.indeterminate = true;
  } else if (stage === 'polling') {
    const total = pollTotal || 0;
    const matched = Math.min(pollMatched || 0, total);
    const p = total > 0 ? 15 + Math.round((matched / total) * 85) : 15;
    step1.percent = p;
    step1.status = `${matched} of ${total} students`;
    step1.indeterminate = total === 0;
  }

  if (step2Complete) {
    step2.percent = 100;
    step2.status = 'Complete';
    step2.done = true;
  } else if (stage === 'resolving_roles') {
    step2.percent = 25;
    step2.status = 'Assigning roles and grouping into competitions';
    step2.indeterminate = true;
  } else if (stage === 'uploading_game') {
    step2.percent = 70;
    step2.status = 'Adding students to teams and competitions';
    step2.indeterminate = true;
  }

  return { step1, step2 };
};

class TeamsRolesSearch extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      query: '',
      typing: false,
      typingTimeout: 0,
      showAddModal: false,
      // Modal step machine:
      //   'choice'          → primary two-card picker (Add New / Modify Roster)
      //   'modify-choice'   → three-card picker under Modify Roster
      //   'add-new'         → full roster upload
      //   'assign-sections' → POST bulk-assign-sections
      //   'reassign'        → POST bulk-assign  (team-only)
      //   'change-roles'    → POST bulk-assign-roles
      modalMode: 'choice',
      selectedFile: null,
      isDragging: false,
      stage: null,
      pollMatched: 0,
      pollTotal: 0,
      validationErrors: null,
      apiError: null,
      successMessage: null,
      // Per-sub-flow state. Stage values: null | 'uploading' |
      // 'success' | 'error'. Result holds the BE's response body for
      // rendering counts.
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    };

    this.fileInputRef = null;
  }

  handleInputChange = (event) => {
    const { handleQueryChange } = this.props;

    const { typingTimeout } = this.state;

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    const newQuery = event.target.value;

    this.setState({
      query: newQuery,
      typing: false,
      typingTimeout: setTimeout(function () {
        handleQueryChange(newQuery);
      }, 2000)
    });
  };

  openAddStudents = () => {
    this.setState({
      showAddModal: true,
      modalMode: 'choice',
      selectedFile: null,
      isDragging: false,
      stage: null,
      pollMatched: 0,
      pollTotal: 0,
      validationErrors: null,
      apiError: null,
      successMessage: null,
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  closeAddStudents = () => {
    if (this.isInProgress()) return;
    this.setState({
      showAddModal: false,
      modalMode: 'choice',
      selectedFile: null,
      isDragging: false,
      stage: null,
      pollMatched: 0,
      pollTotal: 0,
      validationErrors: null,
      apiError: null,
      successMessage: null,
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  // Choice screen card handlers. Clear per-flow state so re-entering
  // a flow (via Back) starts clean.
  chooseAddNew = () => {
    this.setState({
      modalMode: 'add-new',
      selectedFile: null,
      isDragging: false,
      stage: null,
      validationErrors: null,
      apiError: null,
      successMessage: null,
    });
  };

  chooseModifyRoster = () => {
    this.setState({
      modalMode: 'modify-choice',
      selectedFile: null,
      isDragging: false,
      validationErrors: null,
      apiError: null,
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  chooseAssignSections = () => {
    this.setState({
      modalMode: 'assign-sections',
      selectedFile: null,
      isDragging: false,
      validationErrors: null,
      apiError: null,
      sectionsStage: null,
      sectionsResult: null,
    });
  };

  chooseReassign = () => {
    this.setState({
      modalMode: 'reassign',
      selectedFile: null,
      isDragging: false,
      validationErrors: null,
      apiError: null,
      reassignStage: null,
      reassignResult: null,
    });
  };

  chooseChangeRoles = () => {
    this.setState({
      modalMode: 'change-roles',
      selectedFile: null,
      isDragging: false,
      validationErrors: null,
      apiError: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  backToModifyChoice = () => {
    if (this.isInProgress()) return;
    this.setState({
      modalMode: 'modify-choice',
      selectedFile: null,
      isDragging: false,
      validationErrors: null,
      apiError: null,
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  backToChoice = () => {
    if (this.isInProgress()) return;
    this.setState({
      modalMode: 'choice',
      selectedFile: null,
      isDragging: false,
      stage: null,
      validationErrors: null,
      apiError: null,
      successMessage: null,
      reassignStage: null,
      reassignResult: null,
      sectionsStage: null,
      sectionsResult: null,
      rolesStage: null,
      rolesResult: null,
    });
  };

  isInProgress = () => {
    const { stage, reassignStage, sectionsStage, rolesStage } = this.state;
    const addNewInProgress = stage !== null && stage !== 'success' && stage !== 'error';
    const reassignInProgress = reassignStage === 'uploading';
    const sectionsInProgress = sectionsStage === 'uploading';
    const rolesInProgress = rolesStage === 'uploading';
    return addNewInProgress || reassignInProgress || sectionsInProgress || rolesInProgress;
  };

  setFileInputRef = (el) => {
    this.fileInputRef = el;
  };

  openFilePicker = () => {
    if (this.fileInputRef) this.fileInputRef.click();
  };

  handleFileInputChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) this.setState({ selectedFile: file, validationErrors: null, apiError: null });
    if (event.target) event.target.value = '';
  };

  handleRemoveFile = () => {
    this.setState({ selectedFile: null, validationErrors: null, apiError: null });
  };

  handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.state.isDragging) this.setState({ isDragging: true });
  };

  handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isDragging: false });
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) this.setState({ selectedFile: file, validationErrors: null, apiError: null });
  };

  handleUploadClick = () => {
    const { selectedFile } = this.state;
    if (!selectedFile) return;

    this.setState({
      validationErrors: null,
      apiError: null,
      stage: 'parsing',
      pollMatched: 0,
      pollTotal: 0,
    });

    const courseId = Auth.getCourseId();
    // Prefer props (App state) over sessionStorage — Auth.getGameId()
    // returns 0 when sessionStorage was never populated (e.g. no game
    // was surfaced from general-info at App load), which the backend
    // rejects with "Game with ID=0 does not exist". Fall back to
    // Auth.getGameId() so existing users with a valid session don't
    // regress.
    const resolveGameId = () => {
      const propGame = this.props.game;
      const propGames = this.props.games;
      return (propGame && propGame.id)
        || (Array.isArray(propGames) && propGames.length > 0 && propGames[0].id)
        || Auth.getGameId();
    };

    const gameId = resolveGameId();
    if (!gameId) {
      // Recovery path for the "first click after fresh login" race,
      // where general-info hadn't populated game context in App state
      // yet and Auth.getGameId returned 0. Bust the cache, refetch
      // general-info, pick a game from the response, and retry the
      // upload with that id. Previously the only workaround was a
      // manual page refresh. If we still can't find a game after this,
      // surface an actionable error instead of firing the upload with
      // game_id=0.
      clearAppGeneralInfoCache();
      fetchAppGeneralInfo(0, 'instructor')
        .then(data => {
          const { game } = pickCourseAndGame(
            (data && data.courses) || [],
            (data && data.games) || [],
          );
          const resolvedId = game && game.id;
          if (!resolvedId) {
            this.setState({
              stage: 'error',
              apiError: ['We couldn\'t find a game for your course. Please refresh the page and try again.'],
            });
            return;
          }
          Auth.setGameId(resolvedId);
          this.runUpload(selectedFile, courseId, resolvedId);
        })
        .catch(() => {
          this.setState({
            stage: 'error',
            apiError: ['We couldn\'t look up your game. Please refresh the page and try again.'],
          });
        });
      return;
    }

    this.runUpload(selectedFile, courseId, gameId);
  };

  handleReassignUploadClick = () => {
    const { selectedFile } = this.state;
    if (!selectedFile) return;

    const resolveGameId = () => {
      const propGame = this.props.game;
      const propGames = this.props.games;
      return (propGame && propGame.id)
        || (Array.isArray(propGames) && propGames.length > 0 && propGames[0].id)
        || Auth.getGameId();
    };
    const gameId = resolveGameId();
    if (!gameId) {
      this.setState({
        reassignStage: 'error',
        apiError: ['We couldn\'t find a game for your course. Please refresh the page and try again.'],
      });
      return;
    }

    this.setState({
      reassignStage: 'uploading',
      validationErrors: null,
      apiError: null,
      reassignResult: null,
    });

    runReassignUpload({ file: selectedFile, gameId })
      .then(result => {
        this.setState({ reassignStage: 'success', reassignResult: result });
        const { onImportComplete } = this.props;
        if (onImportComplete) onImportComplete();
      })
      .catch(err => {
        if (err && err.kind === 'validation') {
          this.setState({
            reassignStage: null,
            validationErrors: err.errors || [err.message || 'Validation failed.'],
          });
          return;
        }
        const messages = extractBackendErrorMessages(err);
        this.setState({ reassignStage: 'error', apiError: messages });
      });
  };

  resolveGameIdFromProps = () => {
    const propGame = this.props.game;
    const propGames = this.props.games;
    return (propGame && propGame.id)
      || (Array.isArray(propGames) && propGames.length > 0 && propGames[0].id)
      || Auth.getGameId();
  };

  handleSectionsUploadClick = () => {
    const { selectedFile } = this.state;
    if (!selectedFile) return;

    const courseId = Auth.getCourseId();
    const gameId = this.resolveGameIdFromProps();
    if (!gameId) {
      this.setState({
        sectionsStage: 'error',
        apiError: ['We couldn\'t find a game for your course. Please refresh the page and try again.'],
      });
      return;
    }

    this.setState({
      sectionsStage: 'uploading',
      validationErrors: null,
      apiError: null,
      sectionsResult: null,
    });

    runAssignSectionsUpload({ file: selectedFile, courseId, gameId })
      .then(result => {
        this.setState({ sectionsStage: 'success', sectionsResult: result });
        const { onImportComplete } = this.props;
        if (onImportComplete) onImportComplete();
      })
      .catch(err => {
        if (err && err.kind === 'validation') {
          this.setState({
            sectionsStage: null,
            validationErrors: err.errors || [err.message || 'Validation failed.'],
          });
          return;
        }
        if (err && err.kind === 'api') {
          this.setState({ sectionsStage: 'error', apiError: err.errors || [err.message] });
          return;
        }
        const messages = extractBackendErrorMessages(err);
        this.setState({ sectionsStage: 'error', apiError: messages });
      });
  };

  handleRolesUploadClick = () => {
    const { selectedFile } = this.state;
    if (!selectedFile) return;

    const gameId = this.resolveGameIdFromProps();
    if (!gameId) {
      this.setState({
        rolesStage: 'error',
        apiError: ['We couldn\'t find a game for your course. Please refresh the page and try again.'],
      });
      return;
    }

    this.setState({
      rolesStage: 'uploading',
      validationErrors: null,
      apiError: null,
      rolesResult: null,
    });

    runChangeRolesUpload({ file: selectedFile, gameId })
      .then(result => {
        this.setState({ rolesStage: 'success', rolesResult: result });
        const { onImportComplete } = this.props;
        if (onImportComplete) onImportComplete();
      })
      .catch(err => {
        if (err && err.kind === 'validation') {
          this.setState({
            rolesStage: null,
            validationErrors: err.errors || [err.message || 'Validation failed.'],
          });
          return;
        }
        if (err && err.kind === 'api') {
          this.setState({ rolesStage: 'error', apiError: err.errors || [err.message] });
          return;
        }
        const messages = extractBackendErrorMessages(err);
        this.setState({ rolesStage: 'error', apiError: messages });
      });
  };

  runUpload = (file, courseId, gameId) => {
    runRosterUpload({
      file,
      courseId,
      gameId,
      onProgress: (info) => {
        const next = { stage: info.stage };
        if (info.stage === 'polling') {
          next.pollMatched = info.matched != null ? info.matched : 0;
          next.pollTotal = info.total != null ? info.total : 0;
        }
        if (info.stage === 'success') {
          const added = info.addedCount || 0;
          const skipped = info.skippedCount || 0;
          const teams = info.numTeams || 0;
          const comps = info.numCompetitions || 0;
          const teamsLabel = `${teams} team${teams === 1 ? '' : 's'}`;
          const compsLabel = `${comps} competition${comps === 1 ? '' : 's'}`;
          const groupingLabel = `${teamsLabel} across ${compsLabel}`;
          let message;
          if (added === 0 && skipped > 0) {
            message = `All ${skipped} students were already in the course. Assigned to ${groupingLabel}.`;
          } else if (added > 0 && skipped > 0) {
            message = `Added ${added} new student${added === 1 ? '' : 's'} (${skipped} already existed) across ${groupingLabel}.`;
          } else {
            message = `Added ${added} student${added === 1 ? '' : 's'} across ${groupingLabel}.`;
          }
          next.successMessage = message;
        }
        this.setState(next);
      },
    })
      .then(() => {
        this.setState({ stage: 'success' });
        const { onImportComplete } = this.props;
        if (onImportComplete) onImportComplete();
      })
      .catch(err => {
        if (err && err.kind === 'validation') {
          this.setState({
            stage: null,
            validationErrors: err.errors || [err.message || 'Validation failed.'],
          });
        } else {
          const messages = extractBackendErrorMessages(err);
          this.setState({ stage: 'error', apiError: messages });
        }
      });
  };

  renderStep(index, title, state) {
    const isActive = !state.done && state.percent > 0;
    return (
      <StepBlock>
        <StepHeader>
          <StepTitle dimmed={!isActive && !state.done}>
            {state.done && <CheckIcon />}
            <span>Step {index} of 2: {title}</span>
          </StepTitle>
          <StepStatus done={state.done}>{state.status}</StepStatus>
        </StepHeader>
        <ProgressBarTrack>
          <ProgressBarFill percent={state.percent} done={state.done} />
          {state.indeterminate && !state.done && <IndeterminateBar />}
        </ProgressBarTrack>
      </StepBlock>
    );
  }

  renderProgress() {
    const { stage, pollMatched, pollTotal } = this.state;
    const { step1, step2 } = computeStepState(stage, pollMatched, pollTotal);
    return (
      <ProgressWrapper>
        {this.renderStep(1, 'Adding students to your course', step1)}
        {this.renderStep(2, 'Adding students to teams and competitions', step2)}
        <ProgressHint>
          This can take up to a few minutes for large rosters. Please
          keep this window open until it finishes.
        </ProgressHint>
      </ProgressWrapper>
    );
  }

  renderChoiceScreen = () => {
    const roster = this.props.courseMembers || [];
    // Only student rows gate Modify Roster. Instructors / co-instructors /
    // instructor assistants on course_members shouldn't keep the button
    // active when there's nobody to modify. course_members payload uses
    // numeric `type` (1 = Student).
    const studentCount = roster.filter(m => m && Number(m.type) === 1).length;
    const hasStudents = studentCount > 0;
    const modifyTooltip = hasStudents
      ? ''
      : 'Add new students to the course first before modifying the roster.';
    return (
      <Fragment>
        <ModalTitle>Add Students</ModalTitle>
        <ModalBody>
          Are you adding new students to the course, or modifying the existing roster?
        </ModalBody>
        <ChoiceGrid>
          <ChoiceCard type="button" onClick={this.chooseAddNew}>
            <ChoiceCardTitle>Add new students</ChoiceCardTitle>
            <ChoiceCardDescription>
              Upload a CSV to enroll students in the course and place them on teams.
            </ChoiceCardDescription>
          </ChoiceCard>
          <ChoiceCard
            type="button"
            onClick={this.chooseModifyRoster}
            disabled={!hasStudents}
            title={modifyTooltip}
          >
            <ChoiceCardTitle>Modify roster</ChoiceCardTitle>
            <ChoiceCardDescription>
              Assign unassigned students to sections, reassign students to different teams, or change student roles.
            </ChoiceCardDescription>
          </ChoiceCard>
        </ChoiceGrid>
        <ModalActions>
          <CancelButton type="button" onClick={this.closeAddStudents}>
            Cancel
          </CancelButton>
        </ModalActions>
      </Fragment>
    );
  };

  renderModifyChoiceScreen = () => (
    <Fragment>
      <BackLink type="button" onClick={this.backToChoice}>
        ← Back
      </BackLink>
      <ModalTitle>Modify roster</ModalTitle>
      <ModalBody>
        Pick what to update. Each option takes a different CSV.
      </ModalBody>
      <ChoiceGridThree>
        <ChoiceCard type="button" onClick={this.chooseAssignSections}>
          <ChoiceCardTitle>Assign to sections</ChoiceCardTitle>
          <ChoiceCardDescription>
            Upload a CSV to assign unassigned students to sections.
          </ChoiceCardDescription>
        </ChoiceCard>
        <ChoiceCard type="button" onClick={this.chooseReassign}>
          <ChoiceCardTitle>Reassign teams</ChoiceCardTitle>
          <ChoiceCardDescription>
            Upload a CSV to move existing students to a different team.
          </ChoiceCardDescription>
        </ChoiceCard>
        <ChoiceCard type="button" onClick={this.chooseChangeRoles}>
          <ChoiceCardTitle>Change roles</ChoiceCardTitle>
          <ChoiceCardDescription>
            Upload a CSV to update CEO / PD / PM / OP / FI / VI roles.
          </ChoiceCardDescription>
        </ChoiceCard>
      </ChoiceGridThree>
      <ModalActions>
        <CancelButton type="button" onClick={this.closeAddStudents}>
          Cancel
        </CancelButton>
      </ModalActions>
    </Fragment>
  );

  renderAddNewFlow = () => {
    const {
      selectedFile,
      isDragging,
      stage,
      validationErrors,
      apiError,
      successMessage,
    } = this.state;

    const inProgress = this.isInProgress();
    const isSuccess = stage === 'success';
    const isError = stage === 'error';
    const showFileControls = !inProgress && !isSuccess;

    return (
      <Fragment>
        {!inProgress && !isSuccess && (
          <BackLink type="button" onClick={this.backToChoice}>
            ← Back
          </BackLink>
        )}
        <ModalTitle>Add new students</ModalTitle>
        {!isSuccess && (
          <ModalBody>
            Use <TemplateLink type="button" onClick={triggerTemplateDownload}>this template</TemplateLink> to bulk add or modify teams.
          </ModalBody>
        )}
        {showFileControls && !selectedFile && !validationErrors && !apiError && (
          <DropZone
            isDragging={isDragging}
            onClick={this.openFilePicker}
            onDragOver={this.handleDragOver}
            onDragEnter={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <DropZoneIcon>
              <UploadCloudIcon />
            </DropZoneIcon>
            <DropZoneText>Drop your CSV file here or click to browse</DropZoneText>
            <DropZoneHint>Accepts .csv files exported from the template above</DropZoneHint>
          </DropZone>
        )}
        {showFileControls && selectedFile && (
          <AttachedFileRow>
            <FileIconWrapper>
              <FileIcon />
            </FileIconWrapper>
            <FileMeta>
              <FileName>{selectedFile.name}</FileName>
              <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
            </FileMeta>
            <RemoveFileButton
              type="button"
              onClick={this.handleRemoveFile}
              title="Remove file"
            >
              <CloseIcon />
            </RemoveFileButton>
          </AttachedFileRow>
        )}
        {validationErrors && validationErrors.length > 0 && (
          <ErrorList>
            <ErrorHeadline>Please fix the following before uploading:</ErrorHeadline>
            {validationErrors.map((msg, idx) => (
              <ErrorItem key={idx}>{msg}</ErrorItem>
            ))}
          </ErrorList>
        )}
        {isError && apiError && apiError.length > 0 && (
          <ErrorList>
            <ErrorHeadline>Upload failed</ErrorHeadline>
            {apiError.map((msg, idx) => (
              <ErrorItem key={idx}>{msg}</ErrorItem>
            ))}
          </ErrorList>
        )}
        {inProgress && this.renderProgress()}
        {isSuccess && successMessage && (
          <SuccessBox>{successMessage}</SuccessBox>
        )}
        <ModalActions>
          {isSuccess ? (
            <UploadButton type="button" onClick={this.closeAddStudents}>
              Done
            </UploadButton>
          ) : (
            <Fragment>
              <CancelButton
                type="button"
                onClick={this.closeAddStudents}
                disabled={inProgress}
              >
                Cancel
              </CancelButton>
              <UploadButton
                type="button"
                onClick={this.handleUploadClick}
                disabled={!selectedFile || inProgress}
              >
                {inProgress ? 'Uploading...' : 'Upload'}
              </UploadButton>
            </Fragment>
          )}
        </ModalActions>
      </Fragment>
    );
  };

  renderBulkAssignSubFlow = ({
    title,
    bodyCopy,
    stage,
    result,
    onUpload,
    renderSuccess,
  }) => {
    const { selectedFile, isDragging, validationErrors, apiError } = this.state;
    const inProgress = stage === 'uploading';
    const isSuccess = stage === 'success';
    const isError = stage === 'error';
    const showFileControls = !inProgress && !isSuccess;

    return (
      <Fragment>
        {!inProgress && !isSuccess && (
          <BackLink type="button" onClick={this.backToModifyChoice}>
            ← Back
          </BackLink>
        )}
        <ModalTitle>{title}</ModalTitle>
        {!isSuccess && (
          <ModalBody>{bodyCopy}</ModalBody>
        )}
        {showFileControls && !selectedFile && !validationErrors && !apiError && (
          <DropZone
            isDragging={isDragging}
            onClick={this.openFilePicker}
            onDragOver={this.handleDragOver}
            onDragEnter={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <DropZoneIcon>
              <UploadCloudIcon />
            </DropZoneIcon>
            <DropZoneText>Drop your CSV file here or click to browse</DropZoneText>
            <DropZoneHint>Accepts .csv files that match the template above</DropZoneHint>
          </DropZone>
        )}
        {showFileControls && selectedFile && (
          <AttachedFileRow>
            <FileIconWrapper>
              <FileIcon />
            </FileIconWrapper>
            <FileMeta>
              <FileName>{selectedFile.name}</FileName>
              <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
            </FileMeta>
            <RemoveFileButton
              type="button"
              onClick={this.handleRemoveFile}
              title="Remove file"
            >
              <CloseIcon />
            </RemoveFileButton>
          </AttachedFileRow>
        )}
        {validationErrors && validationErrors.length > 0 && (
          <ErrorList>
            <ErrorHeadline>Please fix the following before uploading:</ErrorHeadline>
            {validationErrors.map((msg, idx) => (
              <ErrorItem key={idx}>{msg}</ErrorItem>
            ))}
          </ErrorList>
        )}
        {isError && apiError && apiError.length > 0 && (
          <ErrorList>
            <ErrorHeadline>Upload failed</ErrorHeadline>
            {apiError.map((msg, idx) => (
              <ErrorItem key={idx}>{msg}</ErrorItem>
            ))}
          </ErrorList>
        )}
        {isSuccess && renderSuccess && renderSuccess(result)}
        <ModalActions>
          {isSuccess ? (
            <UploadButton type="button" onClick={this.closeAddStudents}>
              Done
            </UploadButton>
          ) : (
            <Fragment>
              <CancelButton
                type="button"
                onClick={this.closeAddStudents}
                disabled={inProgress}
              >
                Cancel
              </CancelButton>
              <UploadButton
                type="button"
                onClick={onUpload}
                disabled={!selectedFile || inProgress}
              >
                {inProgress ? 'Uploading...' : 'Upload'}
              </UploadButton>
            </Fragment>
          )}
        </ModalActions>
      </Fragment>
    );
  };

  renderReassignFlow = () => this.renderBulkAssignSubFlow({
    title: 'Reassign teams',
    bodyCopy: (
      <Fragment>
        Upload a CSV with an <b>email</b> column plus a <b>team</b> (or <b>team_id</b>) column. Only students already on a team in this game can be reassigned. Use <TemplateLink type="button" onClick={triggerReassignTemplateDownload}>this template</TemplateLink> to get started.
      </Fragment>
    ),
    stage: this.state.reassignStage,
    result: this.state.reassignResult,
    onUpload: this.handleReassignUploadClick,
    renderSuccess: (result) => (
      <SuccessBox>
        <ReassignResultGrid>
          <ReassignResultCell>
            <ReassignResultLabel>Rows processed:</ReassignResultLabel>
            {result && result.rows != null ? result.rows : 0}
          </ReassignResultCell>
          <ReassignResultCell>
            <ReassignResultLabel>Unchanged:</ReassignResultLabel>
            {result && result.unchanged != null ? result.unchanged : 0}
          </ReassignResultCell>
          <ReassignResultCell>
            <ReassignResultLabel>Team updates:</ReassignResultLabel>
            {result && result.team_updates != null ? result.team_updates : 0}
          </ReassignResultCell>
        </ReassignResultGrid>
      </SuccessBox>
    ),
  });

  renderAssignSectionsFlow = () => this.renderBulkAssignSubFlow({
    title: 'Assign to sections',
    bodyCopy: (
      <Fragment>
        Upload a CSV with a <b>username</b> column plus a <b>section</b> (or <b>section_id</b>) column. Every unassigned student must appear exactly once, and no already-assigned student may be in the file. Use <TemplateLink type="button" onClick={triggerAssignSectionsTemplateDownload}>this template</TemplateLink> to get started.
      </Fragment>
    ),
    stage: this.state.sectionsStage,
    result: this.state.sectionsResult,
    onUpload: this.handleSectionsUploadClick,
    renderSuccess: (result) => (
      <SuccessBox>
        {result && typeof result.updated === 'number'
          ? `Assigned ${result.updated} student${result.updated === 1 ? '' : 's'} to sections.`
          : 'Students have been assigned to their sections.'}
      </SuccessBox>
    ),
  });

  renderChangeRolesFlow = () => this.renderBulkAssignSubFlow({
    title: 'Change roles',
    bodyCopy: (
      <Fragment>
        Upload a CSV with an <b>email</b> column and a <b>role</b> column. Role values must be one of <b>CEO, PD, PM, OP, FI, VI</b>. Students must already be on a team in this game. Use <TemplateLink type="button" onClick={triggerChangeRolesTemplateDownload}>this template</TemplateLink> to get started.
      </Fragment>
    ),
    stage: this.state.rolesStage,
    result: this.state.rolesResult,
    onUpload: this.handleRolesUploadClick,
    renderSuccess: (result) => (
      <SuccessBox>
        {result && typeof result.updated === 'number'
          ? `Updated roles for ${result.updated} student${result.updated === 1 ? '' : 's'}.`
          : 'Student roles have been updated.'}
      </SuccessBox>
    ),
  });

  render() {
    const {
      query,
      showAddModal,
      modalMode,
    } = this.state;

    const inProgress = this.isInProgress();

    return (
      <HeaderWrapper>
        <SearchInputWrapper>
          <SearchInputIcon>
            <img src={SearchIcon} alt="Search" />
          </SearchInputIcon>
          <SearchInput
            type="text"
            value={query}
            placeholder="Search by name or email"
            onChange={this.handleInputChange}
          />
        </SearchInputWrapper>
        {!Auth.isAssistantOrCoInstructor() && (
          <PrimaryButton type="button" onClick={this.openAddStudents}>
            <UploadArrowIcon />
            <span>Add Students</span>
          </PrimaryButton>
        )}
        {showAddModal && (
          <ModalOverlay onClick={inProgress ? undefined : this.closeAddStudents}>
            <ModalCard onClick={e => e.stopPropagation()}>
              <HiddenFileInput
                type="file"
                accept=".csv,text/csv"
                innerRef={this.setFileInputRef}
                onChange={this.handleFileInputChange}
              />
              {modalMode === 'choice' && this.renderChoiceScreen()}
              {modalMode === 'modify-choice' && this.renderModifyChoiceScreen()}
              {modalMode === 'add-new' && this.renderAddNewFlow()}
              {modalMode === 'assign-sections' && this.renderAssignSectionsFlow()}
              {modalMode === 'reassign' && this.renderReassignFlow()}
              {modalMode === 'change-roles' && this.renderChangeRolesFlow()}
            </ModalCard>
          </ModalOverlay>
        )}
      </HeaderWrapper>
    );
  }
}

export default TeamsRolesSearch;



// WEBPACK FOOTER //
// ./src/instructor/components/teams-roles/search.js